import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import { environment } from '../../../environment/environment';
import { OrganizationCustomerDTO } from '../../../dto/organization/organization-customer.dto';
import { EventCustomerBillingDTO } from '../../../dto/event/event-customer-billing.dto';
import { DunaCustomersParticipantsDialogComponent } from './duna-customers-participants-dialog.component';
import { DunaCustomersEditDialogComponent } from './duna-customers-edit-dialog.component';
import { DunaCustomersCreateDialogComponent } from './duna-customers-create-dialog.component';
import {
  DunaCustomersImportDialogComponent,
  CustomerImportResult,
} from './duna-customers-import-dialog.component';
import {
  DunaCustomersProformaDialogComponent,
  DUNA_PROFORMA_DIALOG_CONFIG,
} from './duna-customers-proforma-dialog.component';
import {
  DunaCateringPreferencesDialogComponent,
  DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
} from '../duna-catering/duna-catering-preferences-dialog.component';

@Component({
  selector: 'app-duna-customers',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDialogModule,
    DynamicTableComponent,
  ],
  templateUrl: './duna-customers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-customers.component.scss',
})
export class DunaCustomersComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private editListener = (e: Event) => this.handleEdit(e as CustomEvent);
  private deleteListener = (e: Event) => this.handleDelete(e as CustomEvent);
  private participantsListener = (e: Event) =>
    this.handleParticipants(e as CustomEvent);
  private toggleProcessedListener = (e: Event) =>
    this.handleToggleProcessed(e as CustomEvent);
  private toggleApprovedListener = (e: Event) =>
    this.handleToggleApproved(e as CustomEvent);
  private viewEventsListener = (e: Event) =>
    this.handleViewEvents(e as CustomEvent);
  private proformaListener = (e: Event) =>
    this.handleProforma(e as CustomEvent);
  private cateringWishesListener = (e: Event) =>
    this.handleCateringWishes(e as CustomEvent);

  @ViewChild('customersTable') customersTable?: DynamicTableComponent;
  @ViewChild('billingTable') billingTable?: DynamicTableComponent;
  @ViewChild('billingSection') billingSectionRef?: ElementRef<HTMLElement>;

  /** Customer currently pinned in the billing filter (null = show all) */
  selectedCustomer: OrganizationCustomerDTO | null = null;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private componentDataSourceService: ComponentDataSourceService
  ) {}

  ngOnInit(): void {
    window.addEventListener(
      'duna-customers-edit',
      this.editListener as EventListener
    );
    window.addEventListener(
      'duna-customers-delete',
      this.deleteListener as EventListener
    );
    window.addEventListener(
      'duna-customer-events-participants',
      this.participantsListener as EventListener
    );
    window.addEventListener(
      'duna-customer-events-toggle-processed',
      this.toggleProcessedListener as EventListener
    );
    window.addEventListener(
      'duna-customer-events-toggle-approved',
      this.toggleApprovedListener as EventListener
    );
    window.addEventListener(
      'duna-customers-view-events',
      this.viewEventsListener as EventListener
    );
    window.addEventListener(
      'duna-customer-events-proforma',
      this.proformaListener as EventListener
    );
    window.addEventListener(
      'duna-customer-events-catering-wishes',
      this.cateringWishesListener as EventListener
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'duna-customers-edit',
      this.editListener as EventListener
    );
    window.removeEventListener(
      'duna-customers-delete',
      this.deleteListener as EventListener
    );
    window.removeEventListener(
      'duna-customer-events-participants',
      this.participantsListener as EventListener
    );
    window.removeEventListener(
      'duna-customer-events-toggle-processed',
      this.toggleProcessedListener as EventListener
    );
    window.removeEventListener(
      'duna-customer-events-toggle-approved',
      this.toggleApprovedListener as EventListener
    );
    window.removeEventListener(
      'duna-customers-view-events',
      this.viewEventsListener as EventListener
    );
    window.removeEventListener(
      'duna-customer-events-proforma',
      this.proformaListener as EventListener
    );
    window.removeEventListener(
      'duna-customer-events-catering-wishes',
      this.cateringWishesListener as EventListener
    );
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── View customer events ─────────────────────────────────────────────────────

  private handleViewEvents(event: CustomEvent): void {
    const customer = event.detail as OrganizationCustomerDTO;
    this.selectedCustomer = customer;

    // Apply the customer name as the search query on the billing table
    if (this.billingTable) {
      (this.billingTable as any).onSearch(customer.name);
    }

    // Scroll the billing section into view
    setTimeout(() => {
      this.billingSectionRef?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);

    this.cdr.detectChanges();
  }

  clearCustomerFilter(): void {
    this.selectedCustomer = null;
    if (this.billingTable) {
      (this.billingTable as any).onSearch('');
    }
    this.cdr.detectChanges();
  }

  // ── Create ──────────────────────────────────────────────────────────────────

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(DunaCustomersCreateDialogComponent, {
      width: '520px',
    });
    dialogRef.afterClosed().subscribe((created) => {
      if (created) {
        this.customersTable?.refreshTable();
        this.componentDataSourceService.refreshData();
        this.cdr.detectChanges();
      }
    });
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(DunaCustomersImportDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
    });
    dialogRef.afterClosed().subscribe((result: CustomerImportResult | false) => {
      if (result && result.created > 0) {
        this.customersTable?.refreshTable();
        this.componentDataSourceService.refreshData();
        this.cdr.detectChanges();
      }
    });
  }

  // ── Edit ────────────────────────────────────────────────────────────────────

  private handleEdit(event: CustomEvent): void {
    const customer = event.detail as OrganizationCustomerDTO;
    const dialogRef = this.dialog.open(DunaCustomersEditDialogComponent, {
      width: '520px',
      data: { customer },
    });
    dialogRef.afterClosed().subscribe((updated) => {
      if (updated) {
        this.customersTable?.refreshTable();
        this.componentDataSourceService.refreshData();
      }
    });
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  private handleDelete(event: CustomEvent): void {
    const customer = event.detail as OrganizationCustomerDTO;
    const dialogRef = this.dialog.open(ConfirmResetDialogComponent, {
      width: '480px',
      data: {
        title: 'Deaktiver kunde?',
        message: `Er du sikker på, at du vil deaktivere kunden "${customer.name}"?`,
        details: [
          'Kunden skjules fra listen (soft delete) og kan genoprettes af en administrator.',
        ],
        confirmText: 'Deaktiver',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.http
          .delete(
            `${environment.apiUrl}/organizations/customers/${customer.guid}`
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.customersTable?.refreshTable();
              this.componentDataSourceService.refreshData();
              this.cdr.detectChanges();
            },
            error: (err) => console.error('Error deleting customer:', err),
          });
      }
    });
  }

  // ── Participants dialog ──────────────────────────────────────────────────────

  private handleParticipants(event: CustomEvent): void {
    const row = event.detail as EventCustomerBillingDTO;
    this.dialog.open(DunaCustomersParticipantsDialogComponent, {
      width: '560px',
      data: { row },
    });
  }

  // ── Proforma invoice ────────────────────────────────────────────────────────

  private handleProforma(event: CustomEvent): void {
    const row = event.detail as EventCustomerBillingDTO;
    const dialogRef = this.dialog.open(DunaCustomersProformaDialogComponent, {
      ...DUNA_PROFORMA_DIALOG_CONFIG,
      data: { row },
    });
    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.billingTable?.refreshTable();
        this.cdr.detectChanges();
      }
    });
  }

  // ── Catering wishes ─────────────────────────────────────────────────────────

  private handleCateringWishes(event: CustomEvent): void {
    const row = event.detail as EventCustomerBillingDTO;
    this.dialog.open(DunaCateringPreferencesDialogComponent, {
      ...DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
      data: {
        row: {
          event_id: row.event_id,
          guid: row.guid,
          event_name: row.event_name,
          start_date: row.start_date,
          end_date: row.end_date,
          location: null,
        },
      },
    });
  }

  // ── Toggle processed ────────────────────────────────────────────────────────

  private handleToggleProcessed(event: CustomEvent): void {
    const row = event.detail as EventCustomerBillingDTO;
    const newProcessed = !row.billing_processed;

    const dialogRef = this.dialog.open(ConfirmResetDialogComponent, {
      width: '480px',
      data: {
        title: newProcessed
          ? 'Markér som behandlet?'
          : 'Fjern behandlet markering?',
        message: `Event: ${row.event_name || row.guid}`,
        details: [
          `Kunde: ${row.customer?.name}`,
          `Total: ${row.total_price?.toLocaleString('da-DK')} kr.`,
        ],
        confirmText: newProcessed ? 'Markér behandlet' : 'Fjern markering',
        cancelText: 'Annuller',
        confirmColor: 'primary',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.http
          .patch(
            `${environment.apiUrl}/organizations/customers/events/${row.guid}/processed`,
            { processed: newProcessed }
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.billingTable?.refreshTable();
              this.cdr.detectChanges();
            },
            error: (err) =>
              console.error('Error toggling processed status:', err),
          });
      }
    });
  }

  // ── Toggle billing approved ─────────────────────────────────────────────────

  private handleToggleApproved(event: CustomEvent): void {
    const row = event.detail as EventCustomerBillingDTO;
    const newApproved = !row.billing_approved;

    const dialogRef = this.dialog.open(ConfirmResetDialogComponent, {
      width: '480px',
      data: {
        title: newApproved ? 'Godkend til fakturering?' : 'Fjern godkendelse?',
        message: `Event: ${row.event_name || row.guid}`,
        details: [
          `Kunde: ${row.customer?.name}`,
          `Total: ${row.total_price?.toLocaleString('da-DK')} kr.`,
        ],
        confirmText: newApproved ? 'Godkend' : 'Fjern godkendelse',
        cancelText: 'Annuller',
        confirmColor: 'primary',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.http
          .patch(
            `${environment.apiUrl}/organizations/customers/events/${row.guid}/approved`,
            { approved: newApproved }
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.billingTable?.refreshTable();
              this.cdr.detectChanges();
            },
            error: (err) =>
              console.error('Error toggling approved status:', err),
          });
      }
    });
  }
}
